import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ReportPanel } from '@/components/reporting/ReportPanel';
import { toast } from 'sonner';
import { useFilters } from '@/context/FilterContext';
import { trackAction } from '@/hooks/useActivityTracker';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Truck, Package, Clock, CheckCircle2, Search, 
  RefreshCw, Calendar, Phone,
  AlertCircle, History, ChevronLeft, ChevronRight,
  AlertTriangle, ShieldCheck, ShieldX, Loader2, ArrowRight
} from 'lucide-react';
import { ExportButton } from '@/components/ui/export-button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatIST = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  } catch { return dateStr; }
};

const Dispatch = () => {
  const { filters, buildQueryParams } = useFilters();
  const { user } = useAuth();
  const isManagerOrAdmin = user?.role === 'Admin' || user?.role === 'Manager';

  const [summary, setSummary] = useState({ total_won: 0, pending_dispatch: 0, dispatched: 0, needs_migration: 0 });
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLead, setSelectedLead] = useState(null);
  const [dispatchModalOpen, setDispatchModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [dispatchHistory, setDispatchHistory] = useState([]);
  
  // ERP Dispatch state
  const [erpSummary, setErpSummary] = useState({ total: 0, matched: 0, mismatched: 0, unmatched: 0 });
  const [erpRecords, setErpRecords] = useState([]);
  const [erpLoading, setErpLoading] = useState(false);
  const [erpFilter, setErpFilter] = useState('all');
  const [erpPage, setErpPage] = useState(1);
  const [erpTotalPages, setErpTotalPages] = useState(1);
  const [polling, setPolling] = useState(false);
  const [discrepancyModal, setDiscrepancyModal] = useState(null);

  // Dispatch form state
  const [dispatchForm, setDispatchForm] = useState({
    dispatch_status: '',
    dispatch_date: '',
    delivery_address: '',
    transporter_details: '',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const queryParams = buildQueryParams();
      const res = await axios.get(`${API}/dispatch/summary?${queryParams}`, { withCredentials: true });
      setSummary(res.data);
    } catch {
      // silent
    }
  }, [buildQueryParams]);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const status = activeTab === 'all' ? '' : activeTab;
      const queryParams = buildQueryParams();
      const res = await axios.get(`${API}/dispatch/list?${queryParams}`, {
        params: { dispatch_status: status, search, page, limit: 20 },
        withCredentials: true
      });
      setLeads(res.data.leads);
      setTotalPages(res.data.pages);
    } catch {
      toast.error('Failed to fetch leads');
    } finally {
      setLoading(false);
    }
  }, [activeTab, search, page, buildQueryParams]);

  const fetchErpSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/erp-sync/dispatch/summary`, { withCredentials: true });
      setErpSummary(res.data);
    } catch { /* silent */ }
  }, []);

  const fetchErpRecords = useCallback(async () => {
    setErpLoading(true);
    try {
      const params = { page: erpPage, limit: 20 };
      if (erpFilter !== 'all') params.verification = erpFilter;
      const res = await axios.get(`${API}/erp-sync/dispatch/records`, { params, withCredentials: true });
      setErpRecords(res.data.records || []);
      setErpTotalPages(res.data.pages || 1);
    } catch {
      toast.error('Failed to load ERP dispatch records');
    } finally {
      setErpLoading(false);
    }
  }, [erpFilter, erpPage]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { if (activeTab !== 'erp_verified') fetchLeads(); }, [fetchLeads, activeTab]);
  useEffect(() => { if (isManagerOrAdmin) { fetchErpSummary(); } }, [fetchErpSummary, isManagerOrAdmin]);
  useEffect(() => { if (isManagerOrAdmin && activeTab === 'erp_verified') { fetchErpRecords(); } }, [fetchErpRecords, activeTab, isManagerOrAdmin]);

  useEffect(() => { setPage(1); }, [filters]);

  const handleMigrate = async () => {
    try {
      const res = await axios.post(`${API}/dispatch/migrate`, {}, { withCredentials: true });
      toast.success(`Migration complete: ${res.data.total_migrated} orders updated`);
      fetchSummary();
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    }
  };

  const openDispatchModal = (lead, newStatus) => {
    setSelectedLead(lead);
    setDispatchForm({
      dispatch_status: newStatus,
      dispatch_date: newStatus === 'dispatched' ? new Date().toISOString().split('T')[0] : '',
      delivery_address: lead.delivery_address || '',
      transporter_details: lead.transporter_details || '',
      reason: ''
    });
    setDispatchModalOpen(true);
  };

  const handleDispatchSubmit = async () => {
    if (!selectedLead) return;
    if (dispatchForm.dispatch_status === 'dispatched' && !dispatchForm.dispatch_date) {
      toast.error('Dispatch date is required');
      return;
    }
    trackAction('dispatch_update', 'dispatch', selectedLead.lead_id, { status: dispatchForm.dispatch_status });
    setSubmitting(true);
    try {
      await axios.patch(`${API}/dispatch/${selectedLead.lead_id}`, {...dispatchForm, order_id: selectedLead.order_id}, { withCredentials: true });
      toast.success(`Status updated to ${dispatchForm.dispatch_status}`);
      setDispatchModalOpen(false);
      fetchSummary();
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchHistory = async (lead) => {
    try {
      const res = await axios.get(`${API}/dispatch/${lead.lead_id}/history`, { params: {order_id: lead.order_id}, withCredentials: true });
      setDispatchHistory(res.data.history || []);
      setSelectedLead(lead);
      setHistoryModalOpen(true);
    } catch {
      toast.error('Failed to fetch history');
    }
  };

  const handlePollERP = async () => {
    setPolling(true);
    try {
      const res = await axios.post(`${API}/erp-sync/dispatch/poll`, {}, { withCredentials: true });
      toast.success(`Poll complete: ${res.data.matched} matched, ${res.data.mismatched} mismatches, ${res.data.unmatched} unmatched`);
      fetchErpSummary();
      fetchErpRecords();
      fetchSummary();
      fetchLeads();
    } catch {
      toast.error('Failed to poll ERP');
    } finally {
      setPolling(false);
    }
  };

  const handleResolve = async (dispatchId) => {
    try {
      await axios.post(`${API}/erp-sync/dispatch/resolve/${dispatchId}`, {}, { withCredentials: true });
      toast.success('Marked as resolved');
      fetchErpRecords();
      fetchErpSummary();
    } catch {
      toast.error('Failed to resolve');
    }
  };

  const handleForceMatch = async (dispatchId) => {
    if (!window.confirm('This will override your lead data with ERP dispatch data. Continue?')) return;
    try {
      await axios.post(`${API}/erp-sync/dispatch/force-match/${dispatchId}`, {}, { withCredentials: true });
      toast.success('Force accepted — lead updated with ERP data');
      fetchErpRecords();
      fetchErpSummary();
      fetchSummary();
      fetchLeads();
      setDiscrepancyModal(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to force match');
    }
  };

  const hasDiscrepancies = erpSummary.mismatched > 0 || erpSummary.unmatched > 0;

  return (
    <div className="space-y-6 p-6" data-testid="dispatch-page">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Truck className="h-8 w-8 text-primary" />
            Dispatch Management
          </h1>
          <p className="text-muted-foreground">Track and manage order dispatches</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isManagerOrAdmin && (
            <Button onClick={handlePollERP} variant="outline" className="gap-2" disabled={polling} data-testid="poll-erp-dispatch-btn">
              {polling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sync ERP Dispatches
            </Button>
          )}
          <Button onClick={() => { fetchSummary(); fetchLeads(); }} variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* Discrepancy Alert Banner */}
      {isManagerOrAdmin && hasDiscrepancies && (
        <Card className="border-red-300 bg-red-50 dark:bg-red-950/20" data-testid="dispatch-discrepancy-alert">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-red-800 dark:text-red-200">
                  Dispatch Discrepancies Detected
                </p>
                <p className="text-sm text-red-700 dark:text-red-300 mt-0.5">
                  {erpSummary.mismatched > 0 && `${erpSummary.mismatched} KVA/Phase mismatch(es). `}
                  {erpSummary.unmatched > 0 && `${erpSummary.unmatched} unmatched dispatch(es). `}
                  Review and fix in the "ERP Verified" tab.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-100"
              onClick={() => setActiveTab('erp_verified')}
              data-testid="view-discrepancies-btn"
            >
              Review Now
            </Button>
          </CardContent>
        </Card>
      )}

      <ReportPanel id="sales-orders" compact />
      {/* Summary Cards */}
      <div className={cn("grid gap-4", isManagerOrAdmin ? "grid-cols-1 md:grid-cols-5" : "grid-cols-1 md:grid-cols-4")}>
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600" /> Total Won Orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-green-600">{summary.total_won}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-600" /> Pending Dispatch
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-amber-600">{summary.pending_dispatch}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
              <Package className="h-4 w-4 text-blue-600" /> Dispatched
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-blue-600">{summary.dispatched}</p>
          </CardContent>
        </Card>

        {isManagerOrAdmin && (
          <Card className={cn("bg-gradient-to-br", erpSummary.matched > 0 ? "from-emerald-50 to-green-50 border-emerald-200" : "from-gray-50 to-slate-50 border-gray-200")}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-emerald-600" /> ERP Verified
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-emerald-600">{erpSummary.matched}</p>
            </CardContent>
          </Card>
        )}

        {summary.needs_migration > 0 && (
          <Card className="bg-gradient-to-br from-red-50 to-orange-50 border-red-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-muted-foreground flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-600" /> Needs Migration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-red-600">{summary.needs_migration}</p>
              <Button onClick={handleMigrate} size="sm" className="mt-2 bg-red-600 hover:bg-red-700">
                Run Migration
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tabs and Search */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <CardTitle>Orders</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <ExportButton
                data={leads}
                filename="dispatch_orders"
                sheetName="Dispatch Orders"
                columns={[
                  { key: 'enquiry_no', header: 'Enquiry No', width: 15 },
                  { key: 'name', header: 'Customer Name', width: 20 },
                  { key: 'phone_number', header: 'Phone', width: 15 },
                  { key: 'kva', header: 'KVA', width: 10 },
                  { key: 'eo_po_date', header: 'Won Date', width: 15 },
                  { key: 'dispatch_status', header: 'Dispatch Status', width: 15 },
                  { key: 'dispatch_date', header: 'Dispatch Date', width: 15 },
                  { key: 'engine_number', header: 'Engine No', width: 20 },
                  { key: 'alternator_number', header: 'Alternator No', width: 20 },
                  { key: 'dealer', header: 'Dealer', width: 20 },
                  { key: 'state', header: 'State', width: 15 }
                ]}
                size="sm"
              >
                Export to Excel
              </ExportButton>
              {activeTab !== 'erp_verified' && (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search orders..."
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="pl-9 w-64"
                    data-testid="dispatch-search"
                  />
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setPage(1); setErpPage(1); }}>
            <TabsList className="mb-4 flex flex-wrap h-auto max-w-full w-fit justify-start gap-1">
              <TabsTrigger value="pending" data-testid="tab-pending">
                <Clock className="h-4 w-4 mr-2" />
                Pending ({summary.pending_dispatch})
              </TabsTrigger>
              <TabsTrigger value="dispatched" data-testid="tab-dispatched">
                <Package className="h-4 w-4 mr-2" />
                Dispatched ({summary.dispatched})
              </TabsTrigger>
              {isManagerOrAdmin && (
                <TabsTrigger value="erp_verified" data-testid="tab-erp-verified" className="relative">
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  ERP Verified ({erpSummary.total})
                  {hasDiscrepancies && (
                    <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-xs">
                      {erpSummary.mismatched + erpSummary.unmatched}
                    </span>
                  )}
                </TabsTrigger>
              )}
              <TabsTrigger value="all" data-testid="tab-all">
                All ({summary.total_won})
              </TabsTrigger>
            </TabsList>

            {/* ERP Verified Tab */}
            <TabsContent value="erp_verified">
              {/* ERP sub-filters */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {['all', 'matched', 'mismatch', 'unmatched'].map(f => (
                  <Button
                    key={f}
                    variant={erpFilter === f ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setErpFilter(f); setErpPage(1); }}
                    data-testid={`erp-filter-${f}`}
                    className={cn(
                      f === 'mismatch' && erpFilter !== f && 'border-red-300 text-red-700',
                      f === 'unmatched' && erpFilter !== f && 'border-amber-300 text-amber-700',
                      f === 'matched' && erpFilter !== f && 'border-green-300 text-green-700',
                    )}
                  >
                    {f === 'all' ? `All (${erpSummary.total})` :
                     f === 'matched' ? `Matched (${erpSummary.matched})` :
                     f === 'mismatch' ? `Mismatch (${erpSummary.mismatched})` :
                     `Unmatched (${erpSummary.unmatched})`}
                  </Button>
                ))}
              </div>

              {erpLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" /> Loading ERP dispatch records...
                </div>
              ) : erpRecords.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ShieldCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No ERP dispatch records yet</p>
                  <p className="text-sm mt-1">Click "Sync ERP Dispatches" to pull latest data</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead>Enquiry No</TableHead>
                        <TableHead>Product</TableHead>
                        <TableHead>Engine / Alternator</TableHead>
                        <TableHead>Dispatched</TableHead>
                        <TableHead>Lead Match</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {erpRecords.map((rec) => (
                        <TableRow key={rec.dispatch_id} className={cn(
                          rec.verification === 'mismatch' && 'bg-red-50 dark:bg-red-950/10',
                          rec.verification === 'unmatched' && 'bg-amber-50 dark:bg-amber-950/10',
                        )} data-testid={`erp-record-${rec.dispatch_id}`}>
                          <TableCell>
                            {rec.verification === 'matched' && (
                              <Badge className="bg-green-600 gap-1"><ShieldCheck className="h-3 w-3" /> Verified</Badge>
                            )}
                            {rec.verification === 'mismatch' && (
                              <Badge className="bg-red-600 gap-1"><ShieldX className="h-3 w-3" /> Mismatch</Badge>
                            )}
                            {rec.verification === 'unmatched' && (
                              <Badge className="bg-amber-600 gap-1"><AlertTriangle className="h-3 w-3" /> Unmatched</Badge>
                            )}
                            {rec.verification === 'force_accepted' && (
                              <Badge className="bg-blue-600 gap-1"><ShieldCheck className="h-3 w-3" /> Accepted</Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{rec.enquiry_number || '-'}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{rec.kva} KVA {rec.phase}</p>
                              <p className="text-xs text-muted-foreground">{rec.fg_item_name}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {rec.engine_number && <p>Eng: <span className="font-mono">{rec.engine_number}</span></p>}
                              {rec.alternator_number && <p>Alt: <span className="font-mono">{rec.alternator_number}</span></p>}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{formatIST(rec.dispatched_at)}</TableCell>
                          <TableCell>
                            {rec.lead_snapshot?.name ? (
                              <div>
                                <p className="text-sm font-medium">{rec.lead_snapshot.name}</p>
                                <p className="text-xs text-muted-foreground">{rec.lead_snapshot.kva} KVA {rec.lead_snapshot.phase}</p>
                              </div>
                            ) : (
                              <span className="text-sm text-muted-foreground">No match</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {rec.verification === 'mismatch' && !rec.resolved && (
                              <div className="flex gap-1">
                                <Button size="sm" variant="outline" className="text-xs border-red-300"
                                  onClick={() => setDiscrepancyModal(rec)} data-testid={`view-mismatch-${rec.dispatch_id}`}>
                                  View
                                </Button>
                              </div>
                            )}
                            {rec.verification === 'unmatched' && !rec.resolved && (
                              <Button size="sm" variant="outline" className="text-xs border-amber-300"
                                onClick={() => handleResolve(rec.dispatch_id)} data-testid={`resolve-unmatched-${rec.dispatch_id}`}>
                                Dismiss
                              </Button>
                            )}
                            {rec.resolved && rec.verification !== 'matched' && (
                              <span className="text-xs text-muted-foreground">Resolved</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {erpTotalPages > 1 && (
                    <div className="flex flex-wrap gap-2 items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">Page {erpPage} of {erpTotalPages}</p>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setErpPage(p => Math.max(1, p - 1))} disabled={erpPage === 1}>
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setErpPage(p => Math.min(erpTotalPages, p + 1))} disabled={erpPage === erpTotalPages}>
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* Existing tabs: Pending, Dispatched, All */}
            {['pending', 'dispatched', 'all'].map(tabVal => (
              <TabsContent key={tabVal} value={tabVal}>
                {loading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : leads.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No orders found</p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Enquiry No</TableHead>
                          <TableHead>Customer</TableHead>
                          <TableHead>KVA</TableHead>
                          <TableHead>Won Date</TableHead>
                          <TableHead>Dispatch Status</TableHead>
                          <TableHead>Engine / Alt. No</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leads.map((lead) => (
                          <TableRow key={lead.lead_id} data-testid={`dispatch-row-${lead.lead_id}`}>
                            <TableCell className="font-medium">{lead.enquiry_no || '-'}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{lead.name || 'N/A'}</p>
                                <p className="text-xs text-muted-foreground flex items-center gap-1">
                                  <Phone className="h-3 w-3" /> {lead.phone_number || '-'}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>{lead.kva || '-'} KVA</TableCell>
                            <TableCell>{lead.eo_po_date || lead.enquiry_closure_date || '-'}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Badge className={
                                  lead.dispatch_status === 'dispatched' ? 'bg-blue-600' : 
                                  lead.dispatch_status === 'pending' ? 'bg-amber-600' : 'bg-gray-400'
                                }>
                                  {lead.dispatch_status || 'Not Set'}
                                </Badge>
                                {lead.erp_dispatch_verified && (
                                  <ShieldCheck className="h-4 w-4 text-green-600" title="ERP Verified" />
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-xs font-mono">
                                {lead.engine_number && <p>E: {lead.engine_number}</p>}
                                {lead.alternator_number && <p>A: {lead.alternator_number}</p>}
                                {!lead.engine_number && !lead.alternator_number && '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {lead.dispatch_status === 'pending' ? (
                                  <Button size="sm" onClick={() => openDispatchModal(lead, 'dispatched')}
                                    className="bg-blue-600 hover:bg-blue-700" data-testid={`dispatch-btn-${lead.lead_id}`}>
                                    <Truck className="h-4 w-4 mr-1" /> Dispatch
                                  </Button>
                                ) : (
                                  <Button size="sm" variant="outline" onClick={() => openDispatchModal(lead, 'pending')}
                                    data-testid={`pending-btn-${lead.lead_id}`}>
                                    <Clock className="h-4 w-4 mr-1" /> To Pending
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => fetchHistory(lead)}
                                  data-testid={`history-btn-${lead.lead_id}`}>
                                  <History className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {totalPages > 1 && (
                      <div className="flex flex-wrap gap-2 items-center justify-between mt-4">
                        <p className="text-sm text-muted-foreground">Page {page} of {totalPages}</p>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Dispatch Modal */}
      <Dialog open={dispatchModalOpen} onOpenChange={setDispatchModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {dispatchForm.dispatch_status === 'dispatched' ? (
                <><Truck className="h-5 w-5 text-blue-600" /> Mark as Dispatched</>
              ) : (
                <><Clock className="h-5 w-5 text-amber-600" /> Mark as Pending</>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedLead?.name} - {selectedLead?.enquiry_no}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {dispatchForm.dispatch_status === 'dispatched' && (
              <>
                <div>
                  <Label>Dispatch Date *</Label>
                  <Input type="date" value={dispatchForm.dispatch_date}
                    onChange={(e) => setDispatchForm(f => ({ ...f, dispatch_date: e.target.value }))}
                    min={selectedLead?.eo_po_date || selectedLead?.enquiry_closure_date}
                    data-testid="dispatch-date-input" />
                </div>
                <div>
                  <Label>Delivery Address</Label>
                  <Textarea value={dispatchForm.delivery_address}
                    onChange={(e) => setDispatchForm(f => ({ ...f, delivery_address: e.target.value }))}
                    placeholder="Enter delivery address..." data-testid="delivery-address-input" />
                </div>
                <div>
                  <Label>Transporter Details</Label>
                  <Textarea value={dispatchForm.transporter_details}
                    onChange={(e) => setDispatchForm(f => ({ ...f, transporter_details: e.target.value }))}
                    placeholder="Enter transporter name, vehicle no, etc..." data-testid="transporter-input" />
                </div>
              </>
            )}
            {dispatchForm.dispatch_status === 'pending' && selectedLead?.dispatch_status === 'dispatched' && (
              <div>
                <Label>Reason for changing back to pending</Label>
                <Textarea value={dispatchForm.reason}
                  onChange={(e) => setDispatchForm(f => ({ ...f, reason: e.target.value }))}
                  placeholder="Enter reason..." data-testid="reason-input" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDispatchModalOpen(false)}>Cancel</Button>
            <Button onClick={handleDispatchSubmit} disabled={submitting}
              className={dispatchForm.dispatch_status === 'dispatched' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-600 hover:bg-amber-700'}
              data-testid="confirm-dispatch-btn">
              {submitting ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" /> Dispatch History
            </DialogTitle>
            <DialogDescription>
              {selectedLead?.name} - {selectedLead?.enquiry_no}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {dispatchHistory.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No history available</p>
            ) : (
              dispatchHistory.map((entry, idx) => (
                <div key={idx} className="p-3 border rounded-lg bg-muted/30">
                  <div className="flex items-center justify-between">
                    <Badge className={entry.status === 'dispatched' ? 'bg-blue-600' : 'bg-amber-600'}>
                      {entry.status}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {formatIST(entry.changed_at)}
                    </span>
                  </div>
                  <p className="text-sm mt-2">Changed by: {entry.changed_by}</p>
                  {entry.previous_status && <p className="text-xs text-muted-foreground">From: {entry.previous_status}</p>}
                  {entry.reason && <p className="text-sm mt-1 text-amber-600">Reason: {entry.reason}</p>}
                  {entry.dispatch_date && <p className="text-xs text-muted-foreground">Dispatch date: {entry.dispatch_date}</p>}
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Discrepancy Detail Modal */}
      <Dialog open={!!discrepancyModal} onOpenChange={() => setDiscrepancyModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldX className="h-5 w-5" /> Dispatch Discrepancy
            </DialogTitle>
            <DialogDescription>
              Dispatch {discrepancyModal?.dispatch_id} — Enquiry: {discrepancyModal?.enquiry_number}
            </DialogDescription>
          </DialogHeader>

          {discrepancyModal && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 border rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Field</p>
                </div>
                <div className="p-3 border rounded-lg bg-green-50">
                  <p className="text-xs text-green-700 font-medium mb-1">Expected (LMS)</p>
                </div>
                <div className="p-3 border rounded-lg bg-red-50">
                  <p className="text-xs text-red-700 font-medium mb-1">Received (ERP)</p>
                </div>
              </div>
              {discrepancyModal.discrepancies?.map((d, i) => (
                <div key={i} className="grid grid-cols-3 gap-4 text-center items-center">
                  <div className="p-2 font-medium">{
                    { kva: 'KVA', phase: 'Phase', engine_number: 'Engine No.', alternator_number: 'Alternator No.' }[d.field] || d.field
                  }</div>
                  <div className="p-2 bg-green-50 rounded font-mono">{d.expected ?? '-'}</div>
                  <div className="p-2 bg-red-50 rounded font-mono flex items-center justify-center gap-1">
                    <ArrowRight className="h-3 w-3 text-red-500" /> {d.received ?? '-'}
                  </div>
                </div>
              ))}

              <div className="bg-muted/50 rounded-lg p-3">
                <p className="text-sm"><strong>ERP Product:</strong> {discrepancyModal.fg_item_name}</p>
                <p className="text-sm"><strong>Engine:</strong> {discrepancyModal.engine_number || 'N/A'}</p>
                <p className="text-sm"><strong>Alternator:</strong> {discrepancyModal.alternator_number || 'N/A'}</p>
                <p className="text-sm"><strong>Lead:</strong> {discrepancyModal.lead_snapshot?.name} ({discrepancyModal.lead_snapshot?.dealer})</p>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { handleResolve(discrepancyModal?.dispatch_id); setDiscrepancyModal(null); }}>
              Dismiss
            </Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={() => handleForceMatch(discrepancyModal?.dispatch_id)}
              data-testid="force-accept-btn">
              Accept ERP Data
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dispatch;
